"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatPhoneForDisplay } from "@/lib/utils/phone";
import { resolveLoginErrorMessage } from "@/lib/utils/auth-error";
import {
  type AuthFormAppearance,
  loginFormLabelClass,
  loginInputClass,
  loginPhoneBannerClass,
  loginSubmitClass,
} from "./loginAppearance";
import { cn } from "@/lib/utils";

interface AuthPasswordFormProps {
  phone: string;
  onSuccess: () => void;
  onBack: () => void;
  onForgotPassword?: () => void;
  isSendingResetCode?: boolean;
  showBackButton?: boolean;
  appearance?: AuthFormAppearance;
}

export function AuthPasswordForm({
  phone,
  onSuccess,
  onBack,
  onForgotPassword,
  isSendingResetCode = false,
  showBackButton = true,
  appearance = "default",
}: AuthPasswordFormProps) {
  const t = useTranslations();
  const { login, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    password: z
      .string()
      .min(1, t("auth.passwordRequired"))
      .min(6, t("auth.passwordMinLength")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "" },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setError(null);

    const result = await login(phone, values.password);

    if (result.success) {
      onSuccess();
    } else {
      setError(resolveLoginErrorMessage(result.message, t));
    }
  };

  const displayPhone = formatPhoneForDisplay(phone);

  return (
    <div className="space-y-4">
      {showBackButton && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t("common.back")}
        </button>
      )}

      <div
        className={cn(
          "flex items-center justify-center gap-2 text-center",
          loginPhoneBannerClass(appearance),
          appearance === "default" && "p-3",
        )}
      >
        <p className="text-sm opacity-90">{t("auth.loginWith")}</p>
        <p
          className={cn(
            "font-bold text-base",
            appearance === "authPage" ? "text-[#111827]" : "text-primary",
          )}
          dir="ltr"
        >
          {displayPhone}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={loginFormLabelClass(appearance)}>
                  {t("auth.password")}
                </FormLabel>
                <FormControl>
                  <div className="relative !py-0">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={cn("h-12 pe-10", loginInputClass(appearance))}
                      disabled={isLoading}
                      autoFocus
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {onForgotPassword && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                appearance === "authPage" && "text-start text-[#6B7280]",
              )}
            >
              {t("auth.forgotPasswordLabel")}
              {"  "}
              <button
                type="button"
                onClick={() => onForgotPassword()}
                disabled={isSendingResetCode}
                className={cn(
                  "font-semibold hover:underline disabled:opacity-50",
                  appearance === "authPage"
                    ? "text-[#111827] underline-offset-2"
                    : "text-primary",
                )}
              >
                {isSendingResetCode ? (
                  <Loader2 className="inline h-3.5 w-3.5 animate-spin" />
                ) : (
                  t("auth.resetPassword")
                )}
              </button>
            </p>
          )}

          <Button
            type="submit"
            className={loginSubmitClass(appearance)}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin me-2" />
                {t("common.loading")}
              </>
            ) : (
              t("auth.login")
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
